/**
 * Messagerie unique du moniteur : une seule liste et un seul « Nouveau
 * message » à l'écran, pour deux messageries qui restent distinctes côté
 * serveur (équipe : `/v1/messaging` ; élèves : `/v1/student-messaging`).
 * Règles d'écran pures : ni réseau, ni état.
 */
import type { Conversation } from '../api/messaging.types';
import type { Presence } from './presence';
import type {
  ConversationKind,
  StudentConversation,
} from '../api/studentMessaging';

/** Une ligne de la liste unique, avec la messagerie dont elle vient. */
export type ListedConversation =
  | {
      origin: 'team';
      id: string;
      title: string;
      unread: number;
      at: number;
      /** Nature de la conversation (à deux, groupe, salon), quand le serveur la donne. */
      type?: string;
      presence?: Presence | null;
      /** Échange direct : compte de l'interlocuteur (sa photo). */
      peerId?: string | null;
    }
  | {
      origin: 'student';
      id: string;
      title: string;
      unread: number;
      at: number;
      kind: ConversationKind;
      last: StudentConversation['last'];
      presence?: Presence | null;
      /** Élève de l'échange (sa photo) ; absent pour un groupe. */
      candidateId?: string | null;
    };

function time(iso?: string | null): number {
  const value = iso ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(value) ? 0 : value;
}

/**
 * Les deux listes réunies, de l'activité la plus récente à la plus ancienne.
 * Une conversation sans date connue passe après les autres, dans l'ordre reçu.
 */
export function mergeConversations(
  team: Conversation[],
  students: StudentConversation[],
): ListedConversation[] {
  const merged: ListedConversation[] = [
    ...team.map((c) => ({
      origin: 'team' as const,
      id: c.id,
      title: c.title,
      unread: c.unread || 0,
      at: time(c.updatedAt),
      type: c.type,
      presence: c.presence ?? null,
      peerId: c.type === 'DIRECT' ? (c.peerId ?? null) : null,
    })),
    ...students.map((c) => ({
      origin: 'student' as const,
      id: c.id,
      title: c.title,
      unread: c.unread || 0,
      at: time(c.updatedAt) || time(c.last?.at),
      kind: c.kind,
      last: c.last,
      presence: c.presence ?? null,
      candidateId: c.kind === 'GROUP' ? null : (c.candidateId ?? null),
    })),
  ];
  return merged.sort((a, b) => b.at - a.at);
}

/** Ce que « Nouveau message » va créer, selon les personnes choisies. */
export type NewMessagePlan =
  | { kind: 'none' }
  /** Conversation interne de l'équipe, à deux ou en groupe. */
  | { kind: 'team'; type: 'DIRECT' | 'GROUP' }
  /** Échange à deux avec un élève. */
  | { kind: 'student-direct' }
  /** Groupe où au moins un élève lit les messages. */
  | { kind: 'student-group' }
  | { kind: 'blocked'; reason: string };

export function planNewMessage(choice: {
  colleagues: number;
  students: number;
  /** Collègues choisis qui ne font pas partie de l'équipe de l'école des élèves. */
  outsiders: string[];
  canOpenDirect: boolean;
  canCreateGroup: boolean;
}): NewMessagePlan {
  const { colleagues, students, outsiders } = choice;
  if (students === 0) {
    if (colleagues === 0) return { kind: 'none' };
    return { kind: 'team', type: colleagues === 1 ? 'DIRECT' : 'GROUP' };
  }
  if (students === 1 && colleagues === 0) {
    return choice.canOpenDirect
      ? { kind: 'student-direct' }
      : {
          kind: 'blocked',
          reason:
            'Votre compte ne peut pas ouvrir d’échange à deux avec un élève.',
        };
  }
  if (!choice.canCreateGroup)
    return {
      kind: 'blocked',
      reason: 'Votre compte ne peut pas créer de groupe avec des élèves.',
    };
  if (outsiders.length)
    return {
      kind: 'blocked',
      reason: `Hors de l’école de ces élèves : ${outsiders.join(', ')}. Retirez ${
        outsiders.length > 1 ? 'ces personnes' : 'cette personne'
      } pour créer un groupe avec élèves.`,
    };
  return { kind: 'student-group' };
}
